import { AnimationPanel } from '@/components/admin/experiences/page-editor/animation-panel';
import { blockOwnLabel } from '@/components/admin/experiences/page-editor/block-stack';
import { BLOCK_TYPE_ICON } from '@/components/admin/experiences/page-editor/block-type-menu';
import { ConditionsSection } from '@/components/admin/experiences/page-editor/conditions-section';
import {
  ContentPanel,
  type NoyauInspectorContext,
} from '@/components/admin/experiences/page-editor/content-panel';
import {
  ADD_BLOCK_HINT,
  BLOCK_TYPES,
  type InspectorTab,
  PAGE_EDITOR_IDS,
} from '@/components/admin/experiences/page-editor/contracts';
import { VisualPanel } from '@/components/admin/experiences/page-editor/visual-panel';
import { READER_BLOCK_LABEL } from '@/components/reader';
import { declaresField } from '@/lib/block-conversion';
import { cn } from '@/lib/utils';
import type { PaletteContent } from '@/lib/variable-palette';
import type { VariableSources, VariableZone } from '@/lib/variable-pill';
import type {
  AmbianceAsset,
  BlockType,
  ExperienceBlockDraft,
  ExperiencePageDraft,
} from '@matter_extractor/shared';
import { Alert, Button, Icon, Tooltip } from '@pms-digital/design-system';
import type { CSSProperties } from 'react';

/**
 * L'inspecteur d'un bloc : trois onglets et une corbeille (refonte modulaire
 * §9.3; planches `Inspecteurs` et `EditeurPageTelephone`; issue #58).
 *
 * « Contenu, Visuel, Animation (l'ancien onglet Temps). » The three panels are
 * three files; this one is the strip, the title and the one gesture that is not
 * a field. Which tab is open is the CALLER's state and not this component's: a
 * remount on every draft change would throw an operator back to Contenu while
 * they were setting an animation, and the draft changes on every keystroke.
 *
 * The strip is a `nav`-less row of buttons and NOT an ARIA tablist, the same
 * arbitration `versioned-tabs.tsx` makes and for the neighbouring reason: these
 * three are the state of a widget rather than destinations, so they are plain
 * `aria-pressed` buttons - no tab/panel wiring to keep honest, and the panel is
 * the next sibling in reading order.
 *
 * ## « Conditions » closes the Contenu tab (§9.2, issue #150)
 *
 * « L'inspecteur de chaque bloc gagne une section Conditions » : it follows the
 * block's own fields in the Contenu tab, folded while the block carries none
 * (maquette `InspecteurInputNom`) and open as soon as it carries one (maquette
 * `EditeurConditions`). It is mounted with the block's id for a `key`, so its
 * folding is the block's and not the previous selection's.
 *
 * ## Le TYPE ouvre l'onglet Contenu, depuis l'issue #136 (§9.3)
 *
 * « Il faudrait qu'on puisse changer le type d'un bloc » : la rangée des neuf
 * icônes est la première chose de l'onglet, avant les champs qu'elle décide.
 * Une rangée de radios et non une liste déroulante, parce que c'est déjà la
 * forme des huit animations sur cet écran (`animation-picker.tsx`) et que les
 * icônes sont celles de la planche « Ajouter » - un opérateur les a déjà vues
 * au moment de poser le bloc.
 *
 * Le bloc GARDE SON IDENTIFIANT (`convertBlock`), donc un Input devenu Checkbox
 * garde ses citations `{{champ:<id>}}`. Ce qui les fait tomber, c'est un type
 * qui ne déclare plus de champ du tout, et c'est la seule chose que le `Callout`
 * dit - avant le geste, jamais après, et sans rien bloquer : « aucun blocage,
 * aucune boîte » est la décision du grill. Il ne s'affiche donc que quand il y
 * a quelque chose à perdre : un bloc cité qui déclare encore un champ.
 */

const TABS: readonly { id: InspectorTab; label: string }[] = [
  { id: 'contenu', label: 'Contenu' },
  { id: 'visuel', label: 'Visuel' },
  { id: 'animation', label: 'Animation' },
];

export interface BlockInspectorProps {
  readonly page: ExperiencePageDraft;
  readonly block: ExperienceBlockDraft;
  readonly tempsId: string;
  readonly tab: InspectorTab;
  readonly readOnly: boolean;
  /** The `@` menu of this page, by query, and what each token is here (§8.6, D3, D5). */
  readonly palette: (query: string) => PaletteContent;
  readonly zone: VariableZone;
  /** What the conditions' palette reads - the draft and the protocol - and where the page stands (§9.2). */
  readonly sources: VariableSources;
  readonly pageIndex: number;
  /** The OTHER Input already carrying « Nom de la personne dans Sessions », or null (H75). */
  readonly personNameHolder: { readonly label: string; readonly where: string } | null;
  /** What the inspector of a Noyau block reads beyond the block (noyau.md §3.2, #151). */
  readonly noyau: NoyauInspectorContext;
  /** The page is the FEEDBACK page: its Bouton may « Envoyer le retour » (retour-experience.md §7.2). */
  readonly feedbackPage?: boolean;
  /** The theme of the page as CSS variables of the reader - the stars of a Note's « Échelle » (§7.2). */
  readonly previewStyle?: CSSProperties | undefined;
  readonly duplicateName: boolean;
  readonly severalButtons: boolean;
  readonly lastTemps: boolean;
  /**
   * Combien de fois ce bloc est CITÉ dans la timeline (§8.6 ; issue #136) -
   * jetons `{{champ:<id>}}` et conditions d'affichage confondus.
   *
   * Compté par le corps, qui tient le brouillon entier : un inspecteur ne voit
   * que sa page, et une citation vient presque toujours d'une autre.
   */
  readonly citations: number;
  readonly assets: readonly AmbianceAsset[];
  readonly onTab: (next: InspectorTab) => void;
  /** Changer le type en gardant l'identifiant, l'animation et le visuel (#136). */
  readonly onType: (next: BlockType) => void;
  readonly onOpenLibrary: () => void;
  readonly onOpenTemps: (tempsId: string) => void;
  readonly onSetTemps: (tempsId: string) => void;
  readonly onChange: (next: ExperienceBlockDraft) => void;
  readonly onRemove: () => void;
}

export function BlockInspector({
  page,
  block,
  tempsId,
  tab,
  readOnly,
  palette,
  zone,
  sources,
  pageIndex,
  personNameHolder,
  noyau,
  feedbackPage = false,
  previewStyle,
  duplicateName,
  severalButtons,
  lastTemps,
  citations,
  assets,
  onTab,
  onType,
  onOpenLibrary,
  onOpenTemps,
  onSetTemps,
  onChange,
  onRemove,
}: BlockInspectorProps) {
  return (
    <section
      data-testid={PAGE_EDITOR_IDS.inspector}
      data-block-type={block.type}
      className="space-y-3"
    >
      {/* Le type, puis ce que le bloc dit de lui-même, et un tiret tant qu'il ne
          dit rien (#157) : « Texte · Texte » faisait lire le type comme un contenu. */}
      <h2 className="min-w-0 truncate font-semibold text-bleu-nuit text-small">
        {READER_BLOCK_LABEL[block.type]} · {blockOwnLabel(block) || '-'}
      </h2>

      <div className="flex gap-1 border-gray-200 border-b">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            data-testid={PAGE_EDITOR_IDS.inspectorTab}
            data-tab={entry.id}
            aria-pressed={tab === entry.id}
            onClick={() => onTab(entry.id)}
            className={cn(
              '-mb-0.5 cursor-pointer border-b-2 px-2.5 py-1.5 font-semibold text-small transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
              tab === entry.id
                ? 'border-bleu-nuit text-bleu-nuit'
                : 'border-transparent text-gray-600 hover:text-bleu-nuit',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'contenu' ? (
        <>
          <div className="space-y-1.5">
            <p className="font-semibold text-gray-700 text-caption">Type</p>
            <div
              role="radiogroup"
              aria-label="Type du bloc"
              data-testid={PAGE_EDITOR_IDS.blockTypePick}
              className="flex flex-wrap gap-1"
            >
              {BLOCK_TYPES.map((type) => {
                const chosen = type === block.type;
                return (
                  <Tooltip
                    key={type}
                    content={`${READER_BLOCK_LABEL[type]} - ${ADD_BLOCK_HINT[type]}`}
                  >
                    <button
                      type="button"
                      // biome-ignore lint/a11y/useSemanticElements: nine icon buttons, not nine inputs - the icon IS the label.
                      role="radio"
                      aria-checked={chosen}
                      aria-label={READER_BLOCK_LABEL[type]}
                      data-block-type={type}
                      disabled={readOnly}
                      onClick={() => onType(type)}
                      className={cn(
                        'inline-flex size-7 cursor-pointer items-center justify-center rounded-md border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-60',
                        chosen
                          ? 'border-bleu-nuit bg-bleu-nuit text-white'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400',
                      )}
                    >
                      <Icon name={BLOCK_TYPE_ICON[type]} size={14} />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
            {declaresField(block.type) && citations > 0 ? (
              <div data-testid={PAGE_EDITOR_IDS.blockTypeCited}>
                <Alert tone="warning">
                  {citations === 1
                    ? 'Une variable cite ce bloc ailleurs dans l’expérience.'
                    : `${citations} variables citent ce bloc ailleurs dans l’expérience.`}{' '}
                  Le convertir en Checkbox, en Input, en Sélection ou en Note les garde - le bloc
                  conserve son adresse. Tout autre type ne déclare plus de champ : la citation
                  tombera, et l’enregistrement d’une version la nommera.
                </Alert>
              </div>
            ) : null}
          </div>

          <ContentPanel
            block={block}
            readOnly={readOnly}
            palette={palette}
            zone={zone}
            duplicateName={duplicateName}
            severalButtons={severalButtons}
            lastTemps={lastTemps}
            assets={assets}
            personNameHolder={personNameHolder}
            noyau={noyau}
            feedbackPage={feedbackPage}
            previewStyle={previewStyle}
            onOpenLibrary={onOpenLibrary}
            onChange={onChange}
          />
          <div className="border-gray-200 border-t pt-3">
            <ConditionsSection
              key={block.id}
              block={block}
              sources={sources}
              pageIndex={pageIndex}
              readOnly={readOnly}
              onChange={onChange}
            />
          </div>
        </>
      ) : null}

      {tab === 'visuel' ? (
        <VisualPanel
          visual={block.visual}
          readOnly={readOnly}
          /* Un Texte copiable est du texte : son échelle et sa couleur se règlent
             comme celles d'un Texte. Un Lien est peint en BOUTON par le thème,
             donc il suit le Bouton et n'est pas textuel (issue #137). */
          textual={
            block.type === 'text' ||
            block.type === 'checkbox' ||
            block.type === 'input' ||
            block.type === 'copyable'
          }
          onChange={(visual) => onChange({ ...block, visual })}
        />
      ) : null}

      {tab === 'animation' ? (
        <AnimationPanel
          page={page}
          blockId={block.id}
          animation={block.animation}
          tempsId={tempsId}
          readOnly={readOnly}
          animated={block.type !== 'sound'}
          onAnimation={(animation) => onChange({ ...block, animation })}
          onTemps={onSetTemps}
          onOpenTemps={onOpenTemps}
        />
      ) : null}

      <Button
        type="button"
        variant="destructive"
        size="sm"
        data-testid={PAGE_EDITOR_IDS.blockRemove}
        disabled={readOnly}
        onClick={onRemove}
      >
        Supprimer ce bloc
      </Button>
    </section>
  );
}
